package br.com.htms.inspeccaocampo.shared.ui.components

import br.com.htms.inspeccaocampo.ui.kit.TituloSecao
import br.com.htms.inspeccaocampo.ui.kit.PortalButton
import br.com.htms.inspeccaocampo.ui.theme.PortalCores
import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.Card
import androidx.compose.material3.CardDefaults
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.graphics.ImageBitmap
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.unit.dp
import androidx.compose.ui.window.Dialog
import androidx.compose.ui.window.DialogProperties
import br.com.htms.inspeccaocampo.data.FotoAnexo
import br.com.htms.inspeccaocampo.platform.PlatformServices
import br.com.htms.inspeccaocampo.platform.decodeToImageBitmap

@Composable
fun SharedDialogListaFotos(
    titulo: String,
    fotos: List<FotoAnexo>,
    onVisualizar: (FotoAnexo) -> Unit,
    onRemover: (FotoAnexo) -> Unit,
    onFechar: () -> Unit
) {
    CenteredAppDialog(
        onDismissRequest = onFechar,
        title = titulo,
        dismissLabel = "Fechar"
    ) {
        if (fotos.isEmpty()) {
            Text("Nenhuma foto anexada.", style = MaterialTheme.typography.bodyMedium)
        } else {
            Column(
                modifier = Modifier
                    .fillMaxWidth()
                    .heightIn(max = 320.dp)
                    .verticalScroll(rememberScrollState()),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                Text(
                    "${fotos.size} foto(s)",
                    style = MaterialTheme.typography.bodySmall,
                    color = MaterialTheme.colorScheme.onSurfaceVariant
                )
                fotos.forEachIndexed { index, foto ->
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.spacedBy(4.dp),
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Text("Foto ${index + 1}", modifier = Modifier.weight(1f))
                        TextButton(onClick = { onVisualizar(foto) }) { Text("Ver") }
                        TextButton(onClick = { onRemover(foto) }) { Text("Remover") }
                    }
                }
            }
        }
    }
}

@Composable
fun SharedDialogVisualizarFoto(
    foto: FotoAnexo,
    onFechar: () -> Unit
) {
    var bitmap by remember(foto.id, foto.path, foto.conteudoBase64) {
        mutableStateOf<ImageBitmap?>(null)
    }
    var erro by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(foto.id, foto.path, foto.conteudoBase64) {
        erro = null
        bitmap = null
        val bytes = runCatching {
            foto.conteudoBase64
                ?.takeIf { it.isNotBlank() }
                ?.let { PlatformServices.decodeBase64(it) }
                ?: PlatformServices.loadImageBytes(foto.path)
        }.getOrNull()
        if (bytes == null || bytes.isEmpty()) {
            erro = "Não foi possível carregar a foto."
        } else {
            bitmap = decodeToImageBitmap(bytes)
            if (bitmap == null) erro = "Formato de imagem não suportado."
        }
    }

    Dialog(
        onDismissRequest = onFechar,
        properties = DialogProperties(
            usePlatformDefaultWidth = false,
            dismissOnBackPress = true,
            dismissOnClickOutside = true
        )
    ) {
        Box(modifier = Modifier.fillMaxSize()) {
            Box(
                modifier = Modifier
                    .fillMaxSize()
                    .background(Color.Black.copy(alpha = 0.7f))
                    .clickable(
                        interactionSource = remember { MutableInteractionSource() },
                        indication = null,
                        onClick = onFechar
                    )
            )
            Card(
                modifier = Modifier
                    .align(Alignment.Center)
                    .padding(16.dp)
                    .widthIn(max = 560.dp)
                    .fillMaxWidth(),
                shape = RoundedCornerShape(16.dp),
                colors = CardDefaults.cardColors(containerColor = MaterialTheme.colorScheme.surface), border = PortalCores.BordaCartao,
                elevation = CardDefaults.cardElevation(defaultElevation = 8.dp)
            ) {
                Column(
                    modifier = Modifier.padding(16.dp),
                    verticalArrangement = Arrangement.spacedBy(12.dp)
                ) {
                    TituloSecao("Visualizar foto")
                    when {
                        erro != null -> Text(erro!!, color = MaterialTheme.colorScheme.error)
                        bitmap == null -> Text("Carregando…", style = MaterialTheme.typography.bodyMedium)
                        else -> Image(
                            bitmap = bitmap!!,
                            contentDescription = "Foto",
                            modifier = Modifier
                                .fillMaxWidth()
                                .height(360.dp),
                            contentScale = ContentScale.Fit
                        )
                    }
                    PortalButton(onClick = onFechar, modifier = Modifier.fillMaxWidth()) {
                        Text("Fechar")
                    }
                }
            }
        }
    }
}

/** Lista de fotos com Ver / Remover e visualização em tela cheia. */
@Composable
fun SharedFotosMenu(
    titulo: String,
    fotos: List<FotoAnexo>,
    aberto: Boolean,
    onFechar: () -> Unit,
    onRemover: (FotoAnexo) -> Unit
) {
    if (!aberto) return
    var visualizar by remember { mutableStateOf<FotoAnexo?>(null) }
    SharedDialogListaFotos(
        titulo = titulo,
        fotos = fotos,
        onVisualizar = { visualizar = it },
        onRemover = { foto ->
            onRemover(foto)
            if (fotos.size <= 1) onFechar()
        },
        onFechar = onFechar
    )
    visualizar?.let { foto ->
        SharedDialogVisualizarFoto(foto = foto, onFechar = { visualizar = null })
    }
}
